import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { dictionaries, LANGUAGES, type Lang } from './translations';

type Params = Record<string, string | number>;

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Translate a key. Optional params replace `{name}` placeholders in the string. */
  t: (key: string, params?: Params) => string;
  languages: typeof LANGUAGES;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

function getInitialLang(): Lang {
  const stored = localStorage.getItem('lang') as Lang | null;
  if (stored === 'fr' || stored === 'en') return stored;
  return 'fr'; // French is the default language
}

function interpolate(str: string, params?: Params): string {
  if (!params) return str;
  let out = str;
  for (const [k, v] of Object.entries(params)) {
    out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    localStorage.setItem('lang', lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);

  const t = useCallback(
    (key: string, params?: Params) => {
      const dict = dictionaries[lang];
      const raw = dict[key] ?? dictionaries.fr[key] ?? key;
      return interpolate(raw, params);
    },
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT must be used within I18nProvider');
  return ctx;
}

export type { Lang };
